/**
 * Scoring around the header: the progress of a scoring run in flight, labelled
 * by its kind, and the banner for evaluators that have a newer version than
 * their scores (with a re-score for each).
 */

import { Progress } from "@/components/ui/page";
import { useEvaluators } from "@/features/evaluators/hooks";
import { plural } from "@/lib/format";
import { useActiveScoringRun } from "../hooks";
import { latestByKey } from "./rescore-plan";
import { isLive } from "../status";
import type { ExperimentSummary, ScoringRun } from "../types";

const LABEL: Record<ScoringRun["kind"], { running: string; queued: string }> = {
  initial: { running: "Scoring", queued: "Scoring queued" },
  rescore: { running: "Re-scoring", queued: "Re-score queued" },
  retry: { running: "Retrying failed scores", queued: "Retry queued" },
};

/** Checks finished so far in a scoring run (a check is one item × one evaluator). */
export function checksDone(run: ScoringRun): number {
  return run.counts.scored + run.counts.errors + run.counts.not_applicable;
}

/**
 * A scoring run on its own bar: a re-score or retry, or the first scoring of
 * recorded outputs. The first scoring of a live run shows inside the run's
 * progress instead (`RunProgress`), so there is one bar, not two.
 */
export function ScoringProgress({ experiment: e }: { experiment: ExperimentSummary }) {
  const run = useActiveScoringRun(e.id);
  if (!run || (run.kind === "initial" && isLive(e.status))) return null;
  const done = checksDone(run);
  const label = LABEL[run.kind];
  return (
    <section className="box flex flex-col gap-2 px-4 py-3.5" aria-label="Scoring progress">
      <span className="text-[13px]">
        {run.status === "queued" ? label.queued : label.running}:{" "}
        <strong className="font-mono">{done}</strong> of {plural(run.counts.total, "check")}
        <span className="text-muted">
          {" "}
          · {run.evaluators.map((ev) => `${ev.name} v${ev.version}`).join(", ")}
        </span>
      </span>
      <Progress value={done} max={run.counts.total} label="Scoring progress" />
    </section>
  );
}

/** "Correctness v3 available · duplicate the experiment to score with it", one line per stale evaluator. */
export function StaleBanner({ experiment: e }: { experiment: ExperimentSummary }) {
  const stale = e.scoring?.stale ?? [];
  const latest = latestByKey(useEvaluators({ dataset_id: e.dataset?.id, agent_id: e.agent?.id }).data ?? []);
  if (stale.length === 0 || isLive(e.status)) return null;
  return (
    <section
      aria-label="Newer evaluator versions"
      className="flex flex-col gap-1 rounded-[9px] bg-warn-bg px-4 py-3 text-[13px] text-warn"
    >
      {stale.map((key) => {
        const ev = e.evaluators.find((x) => x.key === key);
        const version = latest.get(key)?.version;
        return (
          <span key={key} className="flex flex-wrap items-center gap-1.5">
            <span>
              {ev?.name ?? key}
              {version !== undefined ? ` v${version}` : " has a newer version"} available
            </span>
            <span aria-hidden>·</span>
            <span>duplicate the experiment to score with it</span>
          </span>
        );
      })}
    </section>
  );
}
